/**
 * Utilidades para transformaciones de imágenes de Cloudflare
 * Permite optimizar imágenes automáticamente usando el CDN de Cloudflare
 */

export interface ImageTransformOptions {
  width?: number | 'auto';
  height?: number;
  quality?: number;
  format?: 'auto' | 'webp' | 'avif' | 'jpeg' | 'png' | 'gif';
  fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';
  gravity?: 'auto' | 'left' | 'right' | 'top' | 'bottom' | 'center';
  metadata?: 'keep' | 'copyright' | 'none';
  blur?: number;
  sharpen?: number;
  saturation?: number;
  brightness?: number;
  contrast?: number;
  gamma?: number;
  rotate?: number;
  flip?: 'horizontal' | 'vertical';
  trim?: number;
  onerror?: 'redirect';
}

/**
 * Genera una URL de transformación de imagen de Cloudflare
 * @param imagePath - Ruta de la imagen (sin el dominio)
 * @param options - Opciones de transformación
 * @param domain - Dominio opcional (por defecto usa el dominio actual)
 * @returns URL transformada de Cloudflare
 */
export function getCloudflareImageUrl(
  imagePath: string,
  options: ImageTransformOptions = {},
  domain?: string
): string {
  // Normalizar la ruta de la imagen
  const normalizedPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
  
  // Construir las opciones de transformación
  const transformOptions: string[] = [];
  
  if (options.width) transformOptions.push(`width=${options.width}`);
  if (options.height) transformOptions.push(`height=${options.height}`);
  if (options.quality) transformOptions.push(`quality=${options.quality}`);
  if (options.format) transformOptions.push(`format=${options.format}`);
  if (options.fit) transformOptions.push(`fit=${options.fit}`);
  if (options.gravity) transformOptions.push(`gravity=${options.gravity}`);
  if (options.metadata) transformOptions.push(`metadata=${options.metadata}`);
  if (options.blur) transformOptions.push(`blur=${options.blur}`);
  if (options.sharpen) transformOptions.push(`sharpen=${options.sharpen}`);
  if (options.saturation) transformOptions.push(`saturation=${options.saturation}`);
  if (options.brightness) transformOptions.push(`brightness=${options.brightness}`);
  if (options.contrast) transformOptions.push(`contrast=${options.contrast}`);
  if (options.gamma) transformOptions.push(`gamma=${options.gamma}`);
  if (options.rotate) transformOptions.push(`rotate=${options.rotate}`);
  if (options.flip) transformOptions.push(`flip=${options.flip}`);
  if (options.trim) transformOptions.push(`trim=${options.trim}`);
  if (options.onerror) transformOptions.push(`onerror=${options.onerror}`);
  
  const optionsString = transformOptions.join(',');
  
  // Construir la URL final
  const baseUrl = domain || '';
  return `${baseUrl}/cdn-cgi/image/${optionsString}/${normalizedPath}`;
}

/**
 * Genera URLs responsivas para diferentes tamaños de pantalla
 * @param imagePath - Ruta de la imagen
 * @param sizes - Array de tamaños en píxeles
 * @param options - Opciones adicionales de transformación
 * @param domain - Dominio opcional
 * @returns Objeto con src y srcset
 */
export function getResponsiveImageUrls(
  imagePath: string,
  sizes: number[],
  options: Omit<ImageTransformOptions, 'width'> = {},
  domain?: string
) {
  const urls = sizes.map(size => ({
    url: getCloudflareImageUrl(imagePath, { ...options, width: size }, domain),
    width: size
  }));
  
  const src = urls[0].url;
  const srcset = urls.map(({ url, width }) => `${url} ${width}w`).join(', ');
  
  return { src, srcset };
}

/**
 * Genera una URL optimizada para WebP automático
 * @param imagePath - Ruta de la imagen
 * @param options - Opciones de transformación
 * @param domain - Dominio opcional
 * @returns URL optimizada
 */
export function getOptimizedImageUrl(
  imagePath: string,
  options: Omit<ImageTransformOptions, 'format'> = {},
  domain?: string
): string {
  return getCloudflareImageUrl(imagePath, { ...options, format: 'auto' }, domain);
}

/**
 * Genera una URL para thumbnail con dimensiones específicas
 * @param imagePath - Ruta de la imagen
 * @param width - Ancho del thumbnail
 * @param height - Alto del thumbnail
 * @param domain - Dominio opcional
 * @returns URL del thumbnail
 */
export function getThumbnailUrl(
  imagePath: string,
  width: number,
  height: number,
  domain?: string
): string {
  return getCloudflareImageUrl(imagePath, {
    width,
    height,
    fit: 'cover',
    format: 'auto'
  }, domain);
}

/**
 * Genera una URL para imagen de perfil/avatar
 * @param imagePath - Ruta de la imagen
 * @param size - Tamaño del avatar (cuadrado)
 * @param domain - Dominio opcional
 * @returns URL del avatar
 */
export function getAvatarUrl(
  imagePath: string,
  size: number,
  domain?: string
): string {
  return getCloudflareImageUrl(imagePath, {
    width: size,
    height: size,
    fit: 'cover',
    gravity: 'auto',
    format: 'auto'
  }, domain);
}

/**
 * Genera una URL para imagen de banner/hero
 * @param imagePath - Ruta de la imagen
 * @param width - Ancho del banner
 * @param height - Alto del banner
 * @param domain - Dominio opcional
 * @returns URL del banner
 */
export function getBannerUrl(
  imagePath: string,
  width: number,
  height: number,
  domain?: string
): string {
  return getCloudflareImageUrl(imagePath, {
    width,
    height,
    fit: 'cover',
    gravity: 'auto',
    format: 'auto'
  }, domain);
}

/**
 * Genera una URL para imagen de producto
 * @param imagePath - Ruta de la imagen
 * @param size - Tamaño del producto
 * @param domain - Dominio opcional
 * @returns URL del producto
 */
export function getProductImageUrl(
  imagePath: string,
  size: number,
  domain?: string
): string {
  return getCloudflareImageUrl(imagePath, {
    width: size,
    height: size,
    fit: 'contain',
    format: 'auto'
  }, domain);
}

/**
 * Genera URLs responsivas para imagen de producto
 * @param imagePath - Ruta de la imagen
 * @param domain - Dominio opcional
 * @returns URLs responsivas para producto
 */
export function getResponsiveProductImageUrls(
  imagePath: string,
  domain?: string
) {
  const sizes = [320, 480, 640, 768, 1024, 1280];
  return getResponsiveImageUrls(imagePath, sizes, { fit: 'contain' }, domain);
}

/**
 * Genera URLs responsivas para imagen de banner
 * @param imagePath - Ruta de la imagen
 * @param domain - Dominio opcional
 * @returns URLs responsivas para banner
 */
export function getResponsiveBannerUrls(
  imagePath: string,
  domain?: string
) {
  const sizes = [480, 768, 1024, 1280, 1920];
  return getResponsiveImageUrls(imagePath, sizes, { fit: 'cover' }, domain);
}

/**
 * Genera una URL con ancho automático basado en el dispositivo
 * @param imagePath - Ruta de la imagen
 * @param options - Opciones adicionales
 * @param domain - Dominio opcional
 * @returns URL con ancho automático
 */
export function getAutoWidthImageUrl(
  imagePath: string,
  options: Omit<ImageTransformOptions, 'width'> = {},
  domain?: string
): string {
  return getCloudflareImageUrl(imagePath, { ...options, width: 'auto' }, domain);
}

/**
 * Genera una URL para imagen con calidad optimizada para conexiones lentas
 * @param imagePath - Ruta de la imagen
 * @param options - Opciones de transformación
 * @param domain - Dominio opcional
 * @returns URL optimizada para conexiones lentas
 */
export function getSlowConnectionImageUrl(
  imagePath: string,
  options: ImageTransformOptions = {},
  domain?: string
): string {
  return getCloudflareImageUrl(imagePath, {
    ...options,
    quality: 50,
    format: 'auto'
  }, domain);
} 